import * as Sentry from "@sentry/tanstackstart-react";
import { redirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { isUserAdmin } from "#/modules/admin/index.server";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { getLocaleOnServer } from "#/modules/util.i18n/get-locale.server";
import { isUserImpersonating } from "#/modules/util.impersonate/utils.impersonate.server";

/**
 * Function middleware that enforces the caller has the Admin role on a server function.
 * Composes {@link authMiddleware} (which guarantees a session and exposes `context.tokens`),
 * then verifies the Admin role from the JWT via {@link isUserAdmin}. Attach this to every
 * admin-only server function so the RPC rejects non-admins even when called directly — a route
 * `beforeLoad`/`context.isAdmin` check is UX only and does not protect the endpoint.
 *
 * A non-admin is sent to the app root instead of getting an error. The usual cause is an admin
 * tab left open while another tab started impersonating: the session cookie is shared, so this
 * tab now sends the impersonated docent's token.
 */
export const adminMiddleware = createMiddleware({ type: "function" })
  .middleware([authMiddleware])
  .server(async ({ next, context }) => {
    if (!(await isUserAdmin(context.tokens.accessToken ?? undefined))) {
      Sentry.logger.warn("admin server fn called without the Admin role", {
        impersonating: await isUserImpersonating(),
      });
      throw redirect({ to: "/$locale/{-$schoolyear}", params: { locale: getLocaleOnServer() } });
    }
    return next();
  });
